import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { IconCrossSmall } from '~/ui/core/icons';
import { Switch } from '~/ui/core/switch/Switch';

import { useChatAttachmentOptions } from '../api/use-chat-attachment-options';

// Intersection of what the chat-attachment presign endpoint will accept (backend
// `documents.supported-extensions`) and what the agent's document tools can actually process
// (realtime DocumentTools). Notably excludes legacy `.doc`, which presign would accept but the
// agent refuses to read.
export const CHAT_ATTACHMENT_ACCEPT = ['pdf', 'txt', 'md', 'html', 'docx', 'xlsx', 'xls', 'pptx', 'csv']
    .map((extension) => `.${extension}`)
    .join(',');

// Mirrors MAX_CHAT_ATTACHMENTS (libs/common), the per-message cap the presign batch and the
// message DTOs both enforce.
export const MAX_ATTACHMENT_COUNT = 10;

// Mirrors MAX_DOCUMENT_BYTES (libs/common): the presign endpoint rejects larger files, and the
// agent could not read them anyway.
export const MAX_ATTACHMENT_SIZE_MB = 50;
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export type PendingAttachment = {
    readonly addToKnowledgeBase: boolean;
    /** Set for existing knowledge base documents attached by reference — nothing to upload. */
    readonly documentId?: string;
    /** Set for locally picked files awaiting upload. */
    readonly file?: File;
    readonly fileName: string;
    readonly sizeBytes: number;
};

type PendingAttachmentListProps = {
    readonly attachments: readonly PendingAttachment[];
    readonly onRemove: (fileName: string) => void;
    readonly onToggleKnowledgeBase: (fileName: string, addToKnowledgeBase: boolean) => void;
};

/**
 * Fixed-width cards for documents selected but not yet sent: file name, remove control, and the
 * per-file "Add to Knowledge Base" toggle. Two cards per row, three once the composer is wide
 * enough (wide dock, dashboard tile).
 */
export const PendingAttachmentList: FunctionComponent<PendingAttachmentListProps> = ({
    attachments,
    onRemove,
    onToggleKnowledgeBase,
}) => {
    const translate = useTranslate();
    // Routing an upload into the tenant knowledge base needs CAN_EDIT_JOB_ARCHITECTURE, which the api
    // reports per caller. While the capabilities load (or if they fail) the toggle stays hidden, so
    // everyone can still attach files, but chat-only.
    const { data: attachmentOptions } = useChatAttachmentOptions();
    const canAddToKnowledgeBase = attachmentOptions?.canUploadToKnowledgeBase === true;

    if (attachments.length === 0) return null;

    return (
        <div className="@container w-full">
            <div className="grid w-full grid-cols-2 items-start gap-xs @min-[600px]:grid-cols-3">
                {attachments.map(({ addToKnowledgeBase, documentId, fileName }) => (
                    <div
                        key={fileName}
                        className="flex min-w-0 flex-col gap-xs rounded-md bg-surface-raised px-m py-s shadow-sm"
                    >
                        <div className="flex min-w-0 items-center gap-s">
                            <span className="min-w-0 truncate text-body-xxs font-medium text-text-primary">
                                {fileName}
                            </span>
                            <button
                                aria-label={translate('common.remove')}
                                className="flex items-center border-none bg-transparent p-0 cursor-pointer text-text-muted hover:text-text-primary"
                                type="button"
                                onClick={() => onRemove(fileName)}
                            >
                                <IconCrossSmall size={16} />
                            </button>
                        </div>
                        {documentId !== undefined ? (
                            <span className="text-body-xxs text-text-muted">
                                {translate(
                                    'pages.job-architecture-v2.create-items.upload.selected-from-knowledge-base',
                                )}
                            </span>
                        ) : (
                            canAddToKnowledgeBase && (
                                <div className="flex w-fit items-center gap-s">
                                    <Switch
                                        aria-label={translate(
                                            'pages.job-architecture-v2.create-items.upload.add-to-knowledge-base',
                                        )}
                                        checked={addToKnowledgeBase}
                                        onCheckedChange={(checked) => onToggleKnowledgeBase(fileName, checked)}
                                    />
                                    <span className="text-body-xxs text-text-muted">
                                        {translate(
                                            'pages.job-architecture-v2.create-items.upload.add-to-knowledge-base',
                                        )}
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
